import {
    OtherUsersDashboardsDocument,
    OtherUsersDashboardsQuery,
    useOtherUsersDashboardsQuery,
    UserDashboardsDocument,
    useUserDashboardsQuery,
} from '../generated/graphql'
import { clientSingleton, useApolloClient, type Client } from '@edge/spark/io-apollo'
import { getActiveDashboardId, setActiveDashboardId, useActiveDashboardId } from '../Stores/activeDashboardId'
import { getActiveAuctionId, useActiveAuctionId } from '@edge/spark/io-roles'
import * as utils from '../utils'
import type { DashboardLayout } from '../types'

/** Uses Apollo Client Context through `useOtherUsersDashboardsQuery` & `useUserDashboardsQuery`. Hook returns all dashboards for self or a specific userId */
export const useAllDashboardsQuery = (props?: { userId?: string; skip?: boolean }): DashboardLayout[] => {
    const { userId, skip } = props || {}
    const auctionId = useActiveAuctionId()
    const { data: otherUsersData } = useOtherUsersDashboardsQuery({
        skip: skip || !userId || !auctionId,
        variables: {
            auctionId,
            userId: userId as string, // Will skip if no userId and use ownUserData
        },
    })
    const { data: ownUserData } = useUserDashboardsQuery({ skip: skip || !!userId })

    if (skip) {
        return []
    }
    const rawDashboards = utils.pickDashboards(otherUsersData || ownUserData)
    const normalizedDashboards = utils.normalizeDashboards(rawDashboards)

    return normalizedDashboards
}

/** Uses useApolloClient. Hook returns a function to get all dashboards for self or a specific userId */
export const useGetAllDashboards = (outerProps?: { userId?: string }) => {
    const { userId: outerUserId } = outerProps || {}
    const client = useApolloClient()

    /** Function returns all dashboards for self or a specific userId */
    return async (innerProps?: { userId?: string }) => {
        const { userId: innerUserId } = innerProps || {}
        const userId = innerUserId || outerUserId
        const auctionId = await getActiveAuctionId()
        const { data } = await client.query<OtherUsersDashboardsQuery>({
            query: OtherUsersDashboardsDocument,
            variables: userId ? { auctionId, userId } : {},
            fetchPolicy: 'network-only',
        })
        const rawDashboards = utils.pickDashboards(data)
        const normalizedDashboards = utils.normalizeDashboards(rawDashboards)
        return normalizedDashboards
    }
}

interface GetAllDashboardsProps {
    /** ApolloClient. Attempts to use client singleton if not passed */
    client?: Client
    /** UserId if not getting for own dashboards */
    userId?: string
}

/** Uses passed Client or clientSingleton. Gets all dashboards for self or a specific userId */
export const getAllDashboards = async (props?: GetAllDashboardsProps) => {
    const { userId, client: propsClient } = props || {}
    const client = propsClient || clientSingleton
    const auctionId = await getActiveAuctionId()
    const res = await (userId
        ? client.query({
              query: OtherUsersDashboardsDocument,
              variables: { auctionId, userId },
              fetchPolicy: 'network-only',
          })
        : client.query({ query: UserDashboardsDocument, fetchPolicy: 'network-only' }))

    const rawDashboards = utils.pickDashboards(res.data)
    const normalizedDashboards = utils.normalizeDashboards(rawDashboards)
    return normalizedDashboards
}

interface UseDashboardProps {
    /** Search will prefer `dashId` over `activeDashboardId`. Required if also searching `userId` */
    dashId?: string
    /** Get a specific user's dashboards */
    userId?: string
}

/** Hook finds the current user's active dashboard or the specific dashboard userId & dashId combo if provided. Uses Apollo Client Context via `useAllDashboardsQuery`. */
export const useDashboard = (props?: UseDashboardProps) => {
    const { dashId, userId } = props || {}
    const dashboards = useAllDashboardsQuery({ userId })
    const activeDashboard = utils.findDashboard({ dashboards, dashId })
    return activeDashboard
}

/** Hook returns the current user's active dashboard. Uses Apollo Client Context via `useDashboard`. */
export const useActiveDashboard = () => {
    const activeDashId = useActiveDashboardId()
    return useDashboard({ dashId: activeDashId })
}

interface GetDashboardProps {
    /** ApolloClient. Attempts to use client singleton if not passed */
    client?: Client
    /** Search will prefer `dashId` over `activeDashboardId`. Likely required if also searching `userId` */
    dashId?: string
    /** Get a specific user's dashboards */
    userId?: string
}

/** Finds the current user's active dashboard or the specific dashboard userId & dashId combo. Uses passed Client or clientSingleton via `getAllDashboards`. */
export const getDashboard = async (props?: GetDashboardProps) => {
    const { client, dashId, userId } = props || {}
    const dashboards = await getAllDashboards({ client, userId })
    const activeDashboard = utils.findDashboard({ dashboards, dashId })
    return activeDashboard
}

type GetActiveDashboardProps = Omit<GetDashboardProps, 'userId' | 'dashId'>

/** Finds the current user's active dashboard. Uses passed Client or clientSingleton via `getAllDashboards`. */
export const getActiveDashboard = async (props?: GetActiveDashboardProps) => {
    const { client } = props || {}
    const dashId = getActiveDashboardId()
    const activeDashboard = await getDashboard({ client, dashId })
    return activeDashboard
}

/** Sets the activeDashboardIdStore value to the first dashboard if missing and available */
export const useSetFirstAsActiveDashboard = () => {
    const activeId = useActiveDashboardId()
    const allDashes = useAllDashboardsQuery()

    if (activeId && allDashes.find((d) => d.id === activeId)) {
        return
    }

    if (allDashes.length) {
        const firstId = allDashes[0].id

        if (firstId) {
            setActiveDashboardId(firstId)
        }
    }
}
